import { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail, timeAgo } from "@/lib/api";
import { toast } from "@/lib/toast";

const SEGMENTS = [
  { key: "boletin", label: "Suscriptores del boletín" },
  { key: "cce", label: "Base de datos CCE" },
  { key: "fidem", label: "Directorio FIDEM" },
  { key: "prensa", label: "Medios y prensa" },
];

export default function Correo() {
  const [counts, setCounts] = useState({});
  const [subs, setSubs] = useState([]);
  const [selected, setSelected] = useState(["boletin", "cce"]);
  const [subject, setSubject] = useState("Síntesis Legislativa");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sintesis, setSintesis] = useState(null);
  const [mail, setMail] = useState(null);
  const [importList, setImportList] = useState("cce");
  const [importRows, setImportRows] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [total, setTotal] = useState(null);
  const [audienceError, setAudienceError] = useState("");
  const [teamEmail, setTeamEmail] = useState("");
  const [savingTeam, setSavingTeam] = useState(false);

  const loadSubscribers = () => api.get("/subscribers").then((r) => { setCounts(r.data.counts || {}); setSubs(r.data.subscribers || []); setMail(r.data.mail || null); setTeamEmail(r.data.mail?.team_email || ""); }).catch(() => {});
  useEffect(() => {
    loadSubscribers();
    // Solo se envía una síntesis publicada: un borrador nunca sale por correo.
    api.get("/sintesis", { params: { status: "publicado" } }).then((r) => {
      const s = r.data[0];
      if (s) { setSintesis(s); setSubject(`Síntesis Legislativa — ${s.fecha_label}`); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setTotal(null); setAudienceError("");
    api.get("/campaigns/audience", { params: new URLSearchParams(selected.map(key => ["lists", key])) })
      .then(({ data }) => { if (active) setTotal(data.total); })
      .catch(() => { if (active) setAudienceError("No se pudo calcular el número de destinatarios."); });
    return () => { active = false; };
  }, [selected, counts]);

  const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const plural = (v, one, many) => `${v} ${v === 1 ? one : many}`;
  const COUNTS = [["iniciativas", "iniciativa", "iniciativas"], ["dictamenes", "dictamen", "dictámenes"], ["proposiciones", "proposición", "proposiciones"], ["posicionamientos", "posicionamiento", "posicionamientos"], ["reformas", "reforma", "reformas"]];
  // Igual que la plantilla del servidor (mailer.synthesis_block): solo cifras mayores que cero.
  const resumenLine = (r) => COUNTS.filter(([k]) => r[k] > 0).map(([k, one, many]) => plural(r[k], one, many)).join(" · ") || "Lectura de la sesión";
  const site = window.location.origin;
  const buildHtml = () => {
    const r = sintesis?.resumen || {};
    const dest = (sintesis?.destacado || []).map((d, i) => `${i + 1}. ${esc(d)}`).join("<br>");
    const read = sintesis ? `${site}/sintesis/${sintesis.id}` : site;
    return `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
      <div style="display:flex;justify-content:space-between;margin-bottom:14px"><img src="${site}/assets/logo_cce.png" alt="CCE Tijuana" height="32"><img src="${site}/assets/logo_fidem.png" alt="FIDEM" height="32"></div>
      <div style="font-weight:900;font-size:26px;color:#12395B">Síntesis Legislativa</div>
      <div style="font-weight:800;font-size:13px;margin:6px 0 12px">${esc(sintesis?.fecha_label)}</div>
      <div style="background:#12395B;color:#fff;padding:10px 12px;border-radius:6px;font-size:12px;margin-bottom:12px">
        <b>EN ESTA SÍNTESIS:</b> ${resumenLine(r)}</div>
      <div style="border-left:4px solid #00A651;padding-left:10px;font-size:13px;line-height:1.6">
        <b style="color:#C1272D">DESTACADO</b><br>${dest}</div>
      <p style="text-align:center;margin:22px 0"><a href="${read}" style="background:#00A651;color:#fff;padding:10px 20px;border-radius:4px;font-weight:bold;text-decoration:none">Leer síntesis completa →</a></p>
      ${sintesis ? `<p style="text-align:center;font-size:12px"><a href="${API}/public/sintesis/${sintesis.id}/pdf">Descargar PDF original</a></p>` : ""}
    </div>`;
  };

  const importar = async () => {
    if (!importRows.trim()) return toast("Pega al menos un correo");
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/subscribers/import", { list: importList, rows: importRows });
      setImportResult(data); setImportRows(""); await loadSubscribers();
      toast(`✓ ${data.added} contactos agregados`);
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setImporting(false); }
  };

  const enviar = async () => {
    if (!selected.length) return toast("Selecciona al menos una lista");
    if (!sintesis) return toast("Aún no hay una síntesis publicada para enviar");
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/campaigns/send", { subject, lists: selected, content_html: buildHtml() });
      setResult(data);
      toast(data.simulated ? "✓ Envío simulado registrado" : "✓ Síntesis enviándose vía SendGrid");
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setSending(false); }
  };

  const prueba = async () => {
    if (!sintesis) return toast("Aún no hay una síntesis publicada para enviar");
    try {
      const { data } = await api.post("/campaigns/test", { subject, lists: [], content_html: buildHtml() });
      toast(data.message);
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    }
  };

  const guardarEquipo = async () => {
    setSavingTeam(true);
    try {
      const { data } = await api.put("/mail/settings", { team_email: teamEmail });
      setTeamEmail(data.team_email); setMail((m) => ({ ...m, team_email: data.team_email }));
      toast(data.team_email ? `✓ Alertas y pruebas irán a ${data.team_email}` : "✓ Correo del equipo quitado");
    } catch (e) {
      toast("Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setSavingTeam(false); }
  };

  const r = sintesis?.resumen || {};
  const welcomeLabel = (s) => s.welcome_email?.status === "enviado" ? "Enviada" : s.welcome_email?.status === "fallido" ? "Falló" : "—";

  return (
    <div data-testid="correo-page">
      <h1 className="page">Correo y suscriptores</h1>
      <p className="pagesub">Envía la síntesis legislativa a tu base de datos y suscriptores.</p>
      {mail && mail.managed && <div className="panel" data-testid="mail-managed-status" style={{ borderLeft: "4px solid #00A651" }}>
        <h2>Correo administrado de Emergent · activo</h2>
        <p style={{ fontSize: 13, color: "var(--gray)", margin: "0 0 10px" }}>
          Remitente <b>{mail.from_name}</b>. Salen de verdad: la bienvenida a cada nuevo suscriptor del sitio, tu correo de prueba y la alerta al equipo cuando SUMA registra una propuesta.
          {mail.sendgrid ? " El envío masivo a listas usa tu cuenta de SendGrid." : " El envío masivo a listas importadas sigue en modo simulado hasta que configures tu cuenta de SendGrid."}
        </p>
        <label htmlFor="team-email" style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 6 }}>Correo del equipo (recibe las alertas de propuestas y las pruebas; se usa como Responder a)</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input id="team-email" type="email" value={teamEmail} onChange={(e) => setTeamEmail(e.target.value)} placeholder="equipo@empresa.com" data-testid="team-email-input"
            style={{ flex: "1 1 240px", padding: "10px 12px", border: "1.5px solid #e4e8ec", borderRadius: 6 }} />
          <button className="btn btn-ghost" onClick={guardarEquipo} disabled={savingTeam} data-testid="team-email-save-button">{savingTeam ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>}
      {mail && !mail.configured && <div className="warn-box" data-testid="sendgrid-warning">
        ⚠ Sin correo configurado: los envíos se registran en modo <b>simulado</b>. El correo administrado de Emergent cubre bienvenidas, pruebas y alertas; para el envío masivo a listas agrega tu SENDGRID_API_KEY y correo remitente verificado (SENDER_EMAIL).
      </div>}
      <div className="mail-grid">
        <div>
          <div className="panel">
            <h2>1 · Selecciona los destinatarios</h2>
            <div className="seg-row">
              {SEGMENTS.map((s) => (
                <div key={s.key} className={`seg ${selected.includes(s.key) ? "on" : ""}`}
                  onClick={() => setSelected((sel) => sel.includes(s.key) ? sel.filter((x) => x !== s.key) : [...sel, s.key])}
                  data-testid={`segment-${s.key}`}>
                  <b>{counts[s.key] ?? 0}</b><span>{s.label}</span>
                </div>
              ))}
            </div>
            <h2>2 · Contenido</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                style={{ padding: "11px 14px", border: "1.5px solid #e4e8ec", borderRadius: 6 }} data-testid="campaign-subject-input" />
              <p style={{ fontSize: 13, color: "var(--gray)", margin: 0 }} data-testid="campaign-template-note">Plantilla: Síntesis Legislativa con logos, enlace a la lectura en línea, PDF y liga para darse de baja.{sintesis ? "" : " Publica una síntesis para poder enviarla."}</p>
            </div>
            <h2>3 · Enviar</h2>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn btn-green" onClick={enviar} disabled={sending || total === null || !total || !sintesis} data-testid="send-campaign-button">
                {sending ? "Enviando…" : total === null ? "Calculando destinatarios…" : <>✉ Enviar a {total} contactos</>}
              </button>
              <button className="btn btn-ghost" onClick={prueba} disabled={!sintesis} data-testid="send-test-button">Enviarme prueba</button>
            </div>
            {audienceError && <p role="alert" data-testid="campaign-audience-error">{audienceError}</p>}
            {result && (
              <div className="log" data-testid="send-result" style={{ display: "block" }}>
                {result.simulated ? "⚠ MODO SIMULADO — " : "✓ "} {result.message}
              </div>
            )}
          </div>
          <div className="panel" data-testid="import-panel">
            <h2>Agregar contactos a una lista</h2>
            <p style={{ fontSize: 13, color: "var(--gray)", margin: "0 0 10px" }}>Pega un contacto por línea: «correo» o «nombre, correo» (sirve copiar dos columnas de Excel). Solo agrega personas que aceptaron recibir la síntesis.</p>
            <select value={importList} onChange={(e) => setImportList(e.target.value)} data-testid="import-list-select" style={{ padding: "10px 12px", border: "1.5px solid #e4e8ec", borderRadius: 6, marginBottom: 8 }}>
              {SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <textarea value={importRows} onChange={(e) => setImportRows(e.target.value)} rows={5} placeholder={"Ana Pérez, ana@empresa.com\nluis@camara.org"} data-testid="import-rows-input"
              style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #e4e8ec", borderRadius: 6 }} />
            <button className="btn btn-ghost" onClick={importar} disabled={importing} style={{ marginTop: 8 }} data-testid="import-submit-button">{importing ? "Agregando…" : "Agregar a la lista"}</button>
            {importResult && <div className="log" style={{ display: "block" }} data-testid="import-result">✓ {importResult.added} nuevos · {importResult.existing} ya estaban{importResult.invalid_count ? ` · ${importResult.invalid_count} con correo no válido: ${importResult.invalid.join(" | ")}` : ""}</div>}
          </div>
          <div className="panel">
            <h2>Suscriptores recientes</h2>
            <div style={{ overflowX: "auto" }}>
            <table style={{ whiteSpace: "nowrap" }}>
              <thead><tr><th>Correo</th><th>Lista</th><th>Alta</th><th>Estado</th><th>Bienvenida</th></tr></thead>
              <tbody>
                {subs.slice(0, 8).map((s) => (
                  <tr key={s.id}>
                    <td>{s.email}</td>
                    <td>{SEGMENTS.find((x) => x.key === s.list)?.label.split(" ")[0] || s.list}</td>
                    <td>{timeAgo(s.created_at)}</td>
                    <td><span className={`pill ${s.status === "activo" ? "pub" : "rev"}`}>{s.status === "activo" ? "Activo" : s.status === "baja" ? "Baja" : "Pendiente"}</span></td>
                    <td data-testid={`subscriber-welcome-${s.id}`}>{welcomeLabel(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
        <div>
          <div className="panel">
            <h2>Vista previa del correo</h2>
            <div className="mail-prev">
              <div className="mp-bar"><b>De:</b> {mail?.managed ? `${mail.from_name} (correo administrado de Emergent)` : <>Legis BC &lt;{mail?.sender || "remitente por configurar"}&gt;</>}<br /><b>Asunto:</b> {subject}</div>
              <div style={{ background: "var(--sage)", display: "flex", justifyContent: "space-between", padding: "8px 14px" }}>
                <img src="/assets/logo_cce.png" style={{ height: 30, width: "auto" }} alt="CCE" />
                <img src="/assets/logo_fidem.png" style={{ height: 30, width: "auto" }} alt="FIDEM" />
              </div>
              <div style={{ padding: 18 }}>
                <div style={{ fontWeight: 900, fontSize: 22, color: "var(--navy)", lineHeight: 1.05 }}>Síntesis<br />Legislativa</div>
                <div style={{ fontWeight: 800, fontSize: 12, margin: "6px 0 10px" }}>{sintesis?.fecha_label || "—"}</div>
                <div style={{ background: "var(--navy)", color: "#fff", padding: "10px 12px", borderRadius: 6, fontSize: 11.5, marginBottom: 10 }}>
                  <b>EN ESTA SÍNTESIS:</b> {resumenLine(r)}
                </div>
                <div style={{ borderLeft: "4px solid var(--green)", paddingLeft: 10, fontSize: 12, lineHeight: 1.5 }}>
                  <b style={{ color: "var(--red)" }}>DESTACADO</b><br />
                  {(sintesis?.destacado || []).map((d, i) => <span key={i}>{i + 1}. {d}<br /></span>)}
                </div>
                <div style={{ textAlign: "center", marginTop: 14 }}>
                  <span style={{ background: "var(--green)", color: "#fff", padding: "9px 18px", borderRadius: 4, fontWeight: 800, fontSize: 12 }}>Leer síntesis completa →</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
